import { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import { MenuItem } from "./MenuItem/MenuItem";
import items from "./DataSideBar";
import { Icon } from "../../components/Icon/Icon";
import { breakpoints, layout } from "../../styles/breakpoints";
import { useAuth } from "../../auth/useAuth";

const RAIL_WIDTH = layout.sidebarRail;
const EXPANDED_WIDTH = layout.sidebarExpanded;
const STORAGE_KEY = "sidebar:expanded";

/**
 * Splits a path into segments and replaces numeric ids with a placeholder, so
 * `/compagnies/42/departments` can be compared against the menu entry built
 * from the current company id.
 */
const normalizePath = (path: string): string[] =>
  path
    .split("/")
    .filter(Boolean)
    .map((segment) => (/^\d+$/.test(segment) ? ":id" : segment));

const isPrefixOf = (candidate: string[], target: string[]): boolean =>
  candidate.length > 0 &&
  candidate.length <= target.length &&
  candidate.every((segment, index) => segment === target[index]);

type Props = {
  children?: React.ReactNode;
  styles?: any;
  /** Optional label for the mobile top bar; falls back to the active item. */
  title?: string;
};

export const Sidebar: React.FC<Props> = ({ children, styles, title }) => {
  const classes = useStyles();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { can, canAny } = useAuth();

  /*
   * Only advertise destinations the operator may actually open. Without this
   * the menu listed every screen for every session and clicking one landed on
   * a denial panel - the menu was effectively lying about what was available.
   *
   * The permission on each entry must stay in step with the one on the
   * matching <PrivateRoute> in Routes.tsx.
   */
  const visibleItems = useMemo(
    () =>
      items.filter((item) => {
        if (item.anyOf?.length) return canAny(item.anyOf);
        if (item.permission) return can(item.permission);
        return true;
      }),
    [can, canAny]
  );

  // Grouping is declared on the item rather than derived from its index, so
  // filtering above can never shuffle entries across the divider.
  const mainItems = useMemo(
    () => visibleItems.filter((item) => item.group !== "bottom"),
    [visibleItems]
  );
  const bottomItems = useMemo(
    () => visibleItems.filter((item) => item.group === "bottom"),
    [visibleItems]
  );

  /* `md` and below: the sidebar becomes an overlay drawer. */
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.md}px)`);

  /*
   * Desktop: whether labels are shown. Persisted, so the choice survives a
   * page change or a refresh instead of resetting on every navigation.
   */
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  /* Mobile: whether the drawer is currently slid in. */
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    i18n.changeLanguage("fr");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isExpanded));
    } catch {
      /* Private browsing / storage disabled — the toggle still works. */
    }
  }, [isExpanded]);

  /* Close the drawer whenever the route changes, so it never covers the page
     the user just navigated to. */
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [pathname]);

  /* Leaving mobile width with the drawer open would leave a stale backdrop. */
  useEffect(() => {
    if (!isMobile) setIsDrawerOpen(false);
  }, [isMobile]);

  /* Escape closes the drawer — expected of any modal overlay. */
  useEffect(() => {
    if (!isDrawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDrawerOpen]);

  /* Stop the page behind the overlay from scrolling under the user's finger. */
  useEffect(() => {
    document.body.classList.toggle("no-scroll", isDrawerOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [isDrawerOpen]);

  /*
   * The menu entry matching the current URL. When several entries match
   * (`/compagnies` and `/compagnies/:id/departments`), the most specific one
   * wins.
   */
  const activeItemIndex = useMemo(() => {
    const target = normalizePath(pathname);
    let bestIndex = -1;
    let bestLength = 0;
    visibleItems.forEach((item, index) => {
      if (!item?.to) return;
      const candidate = normalizePath(item.to);
      if (isPrefixOf(candidate, target) && candidate.length > bestLength) {
        bestIndex = index;
        bestLength = candidate.length;
      }
    });
    return bestIndex;
  }, [pathname, visibleItems]);

  const activeLabel =
    activeItemIndex >= 0 ? visibleItems[activeItemIndex].label : "";

  /* On desktop the sidebar shows labels only when expanded; inside the mobile
     drawer labels are always shown. */
  const showLabels = isMobile || isExpanded;

  const onToggle = useCallback(() => {
    if (isMobile) setIsDrawerOpen((open) => !open);
    else setIsExpanded((expanded) => !expanded);
  }, [isMobile]);

  const handleNavigate = useCallback(
    (to?: string) => {
      if (isMobile) setIsDrawerOpen(false);
      /* Previously every click also collapsed the sidebar, so the labels the
         user had just opened vanished. The desktop state is now left alone. */
      if (to) navigate(to);
    },
    [isMobile, navigate]
  );

  const handleLogout = useCallback(() => {
    localStorage.clear();
    window?.location?.reload();
  }, []);

  const renderItem = (item: (typeof items)[number], index: number) => (
    <MenuItem
      key={item?.iconName}
      iconName={item?.iconName ?? ""}
      isCollapsed={showLabels}
      isActive={index === activeItemIndex}
      text={item?.label ?? ""}
      width={item?.width ?? 24}
      height={item?.height ?? 24}
      onClick={() => handleNavigate(item?.to)}
    />
  );

  // Active-state comparison is against the index within `visibleItems`, so the
  // renderers below look their own item up in that same array.
  const renderFrom = (list: typeof items) =>
    list.map((item) => renderItem(item, visibleItems.indexOf(item)));

  return (
    <div
      className={classNames(classes.shell, styles?.sidebarRoot)}
      /*
       * The sidebar offset lives here as padding, NOT on <main> as a margin.
       *
       * With a margin on <main>, the page's own root class is merged onto that
       * same element - and any page declaring `width: 100%` resolved it
       * against the full viewport, then had the margin added on top. The page
       * ended up wider than the shell, which clips, so its right-hand edge was
       * silently cut off and unreachable.
       *
       * As padding on the shell, the offset shrinks <main>'s containing block
       * instead, so `width: 100%` inside a page now means what its author
       * expects and cannot reintroduce the overflow.
       *
       * Below `md` the drawer floats above the content (position: fixed), so
       * there is no rail to offset against.
       */
      style={{
        paddingLeft: isMobile
          ? undefined
          : showLabels
          ? EXPANDED_WIDTH
          : RAIL_WIDTH,
      }}
    >
      {/* Mobile top bar — the only way to reach navigation below `md`. */}
      <header className={classes.mobileBar}>
        <button
          type="button"
          className={classes.burger}
          onClick={onToggle}
          aria-label={
            isDrawerOpen
              ? t("sidebar.closeMenu", "Close menu")
              : t("sidebar.openMenu", "Open menu")
          }
          aria-expanded={isDrawerOpen}
          aria-controls="app-sidebar"
        >
          <Icon
            name={isDrawerOpen ? "close" : "menu"}
            width={24}
            height={24}
          />
        </button>
        <span className={classes.mobileTitle}>{title || activeLabel}</span>
      </header>

      {/* Backdrop: tapping outside the drawer dismisses it. */}
      {isMobile && isDrawerOpen && (
        <div
          className={classes.backdrop}
          onClick={() => setIsDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav
        id="app-sidebar"
        aria-label={t("sidebar.mainNavigation", "Main navigation")}
        className={classNames(
          classes.drawerPositioner,
          isDrawerOpen && classes.drawerOpen
        )}
      >
        <div
          className={classes.container}
          /*
           * On mobile the drawer is always the expanded width, and that width
           * comes from the stylesheet. Emitting an inline width here too meant
           * the stylesheet had to fight it back with `!important`; passing
           * `undefined` leaves the CSS in charge instead.
           */
          style={{
            width: isMobile
              ? undefined
              : showLabels
              ? EXPANDED_WIDTH
              : RAIL_WIDTH,
          }}
        >
          <div className={classes.menuItemContainer}>
            <div className={classes.menuGroup}>
              {/* Expand / collapse control. Hidden on mobile, where the
                  hamburger in the top bar already does this job. */}
              {!isMobile && (
                <MenuItem
                  iconName={isExpanded ? "textWrap" : "textWrapDisabled"}
                  isCollapsed={showLabels}
                  text={
                    isExpanded
                      ? t("sidebar.collapse", "Réduire")
                      : t("sidebar.expand", "Déplier")
                  }
                  width={24}
                  height={24}
                  onClick={onToggle}
                  inverse={isExpanded}
                />
              )}
              {renderFrom(mainItems)}
            </div>

            <div className={classes.menuGroupBottom}>
              {renderFrom(bottomItems)}
              <MenuItem
                key={"logout"}
                iconName={"logout"}
                isCollapsed={showLabels}
                text={t("sidebar.logout", "Logout")}
                width={24}
                height={24}
                onClick={handleLogout}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* Page content */}
      <main className={classNames(classes.content, styles?.sidebarRightRoot)}>
        {children}
      </main>
    </div>
  );
};
